import { CartUpdateEvent } from '@theme/events';

/**
 * Keeps a free-gift line in step with the rest of the cart.
 *
 * Rule: for every unit of any other product in the cart there is one unit of the
 * gift variant. Add a scroll and the gift quantity follows; remove scrolls and it
 * follows back down; empty the cart of scrolls and the gift line is removed.
 *
 * Configured from theme settings, read off the #gift-with-purchase-config script
 * rendered by snippets/scripts.liquid.
 *
 * Deliberate choices:
 *
 * - The cart is always re-fetched from /cart.js rather than trusting the event
 *   payload. /cart/add.js resolves with the line items that were just added, not
 *   with the whole cart, so counting from it would undercount.
 * - `syncing` guards the listener against its own CartUpdateEvent, and the event
 *   we emit is tagged with a source we ignore. Without both, every correction
 *   would trigger another correction.
 * - This runs in the browser, so it is a merchandising convenience, not an
 *   enforcement mechanism. A shopper who edits the cart with JS disabled can
 *   reach checkout without the gift, or with the wrong quantity. If the gift must
 *   be guaranteed, that belongs in a Shopify Function / cart transform.
 */

const config = readConfig();
let syncing = false;

function readConfig() {
  const node = document.getElementById('gift-with-purchase-config');
  if (!node?.textContent) return null;

  try {
    const parsed = JSON.parse(node.textContent);
    const variantId = Number(parsed.variantId);
    if (!parsed.enabled || !Number.isFinite(variantId) || variantId <= 0) return null;

    const ratio = Number(parsed.ratio);
    return {
      variantId,
      ratio: Number.isFinite(ratio) && ratio > 0 ? ratio : 1,
    };
  } catch {
    return null;
  }
}

function cartRoute(name) {
  const routes = /** @type {any} */ (window).Theme?.routes;
  if (routes?.[name]) return routes[name];

  const root = /** @type {any} */ (window).Shopify?.routes?.root || '/';
  const fallback = { cart_url: 'cart', cart_add_url: 'cart/add.js', cart_change_url: 'cart/change' };
  return `${root}${fallback[name]}`;
}

async function fetchCart() {
  const response = await fetch(`${cartRoute('cart_url')}.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`cart fetch failed: ${response.status}`);
  return response.json();
}

/**
 * Section ids of every cart-items component currently on the page.
 *
 * Passing these to the cart API makes it return the re-rendered section HTML in
 * the same response. component-cart-items.js uses `event.detail.data.sections`
 * when it is present and otherwise fires its own renderSection request — so
 * without this the gift line only appeared after a second network round-trip,
 * which is what made it lag behind in the drawer.
 */
function cartSectionIds() {
  const ids = [];
  for (const node of document.querySelectorAll('cart-items-component')) {
    if (node instanceof HTMLElement && node.dataset.sectionId) ids.push(node.dataset.sectionId);
  }
  return ids;
}

async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    // A gift product that is not published to the Online Store sales channel
    // fails here with 422 — the most likely cause if the line never appears.
    console.error('[gift-with-purchase] cart request failed', response.status, payload);
    throw new Error(`cart request failed: ${response.status}`);
  }
  return payload;
}

/**
 * The full cart carried by a cart:update event, or null if it only carries a
 * fragment.
 *
 * /cart/change.js resolves with the whole cart, and component-cart-items.js
 * passes that straight through — so for the quantity steppers in the drawer the
 * cart is already in hand and re-fetching it is a wasted round-trip. /cart/add.js
 * resolves with just the added lines, which is why the shape is checked rather
 * than assumed: `item_count` is present on a full cart and absent on that
 * fragment.
 *
 * @param {Event} event
 */
function cartFromEvent(event) {
  const resource = /** @type {any} */ (event)?.detail?.resource;
  if (resource && Array.isArray(resource.items) && typeof resource.item_count === 'number') {
    return resource;
  }
  return null;
}

/** @param {any} [knownCart] - Cart already in hand; skips the /cart.js fetch. */
async function sync(knownCart) {
  if (!config || syncing) return;

  let cart = knownCart;
  if (!cart) {
    try {
      cart = await fetchCart();
    } catch (error) {
      console.error('[gift-with-purchase]', error);
      return;
    }
  }

  let otherQuantity = 0;
  let giftLine = null;

  for (const item of cart.items || []) {
    if (item.variant_id === config.variantId || item.id === config.variantId) {
      giftLine = item;
      continue;
    }
    otherQuantity += item.quantity;
  }

  const wanted = otherQuantity * config.ratio;
  const current = giftLine ? giftLine.quantity : 0;
  if (wanted === current) return;

  syncing = true;
  try {
    let result;
    const sections = cartSectionIds().join(',');

    if (giftLine) {
      // Addressed by line key rather than variant id: a variant can occupy more
      // than one line when line item properties differ, and the key is exact.
      result = await postJson(`${cartRoute('cart_change_url')}.js`, {
        id: giftLine.key,
        quantity: wanted,
        sections,
      });
    } else if (wanted > 0) {
      result = await postJson(cartRoute('cart_add_url'), {
        items: [{ id: config.variantId, quantity: wanted }],
        sections,
      });
    }

    if (result) {
      document.dispatchEvent(
        new CartUpdateEvent(result, 'gift-with-purchase', {
          source: 'gift-with-purchase',
          // Handing the rendered sections over lets the drawer morph straight
          // away instead of fetching them itself.
          sections: result.sections,
        })
      );
    }
  } catch (error) {
    console.error('[gift-with-purchase]', error);
  } finally {
    syncing = false;
  }
}

if (config) {
  document.addEventListener('cart:update', (event) => {
    // Ignore the event this module emits, or the correction loops forever.
    if (/** @type {any} */ (event).detail?.data?.source === 'gift-with-purchase') return;
    sync(cartFromEvent(event));
  });

  // Catch carts that drifted out of step in an earlier session.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sync(), { once: true });
  } else {
    sync();
  }
}

